import type {
  ActivityAdapter,
  ActivityCompletionMetrics
} from "@/app/integration/contracts";
import { getFriendshipHintCue } from "@/audio/curriculum-catalog/focus-social-feedback-cues";
import { advanceHintEscalation } from "@/game/feedback";
import {
  createScenarioState,
  getScenarioAttemptMetrics,
  reduceScenario,
  scenarioPayloadSchema,
  type ScenarioAction,
  type ScenarioPayload,
  type ScenarioState
} from "@/game/templates/scenario";

export interface FocusSocialFriendshipStoryState {
  readonly scenario: ScenarioState;
  readonly completionConfirmed: boolean;
}

export type FocusSocialFriendshipStoryAction =
  | ScenarioAction
  | { readonly type: "request-hint" }
  | { readonly type: "continue" };

export interface FocusSocialFriendshipChoiceModel {
  readonly audioPath: string | null;
  readonly id: string;
  readonly label: string;
  readonly isSelected: boolean;
}

export interface FocusSocialFriendshipFeedbackModel {
  readonly audioPath: string | null;
  readonly kind: "hint" | "support" | "success";
  readonly message: string;
  readonly playbackRequest: number;
}

export interface FocusSocialFriendshipStoryRenderModel {
  readonly status: "choosing" | "reviewing" | "completed";
  readonly narration: {
    readonly audioPath: string | null;
    readonly text: string;
  };
  readonly choices: readonly FocusSocialFriendshipChoiceModel[];
  readonly feedback: FocusSocialFriendshipFeedbackModel | null;
  readonly followUp: {
    readonly audioPath: string | null;
    readonly playbackRequest: number;
    readonly text: string;
  } | null;
}

function createFriendshipStoryState(): FocusSocialFriendshipStoryState {
  return {
    scenario: createScenarioState(),
    completionConfirmed: false
  };
}

function toFeedbackModel(
  state: ScenarioState
): FocusSocialFriendshipFeedbackModel | null {
  const outcome = state.outcome;
  const playbackRequest = state.attemptCount + state.hintState.hintCount;

  if (outcome !== null) {
    return {
      audioPath: outcome.supportiveFeedback.audio ?? null,
      kind: outcome.isBestChoice ? "success" : "support",
      message: outcome.supportiveFeedback.text,
      playbackRequest
    };
  }

  if (state.hintState.stage === "none") {
    return null;
  }

  const cue = getFriendshipHintCue(state.hintState.stage);

  return {
    audioPath: cue.audioPath,
    kind: "hint",
    message: cue.text,
    playbackRequest
  };
}

function toRenderModel(
  state: FocusSocialFriendshipStoryState,
  payload: ScenarioPayload
): FocusSocialFriendshipStoryRenderModel {
  const scenario = state.scenario;
  const status = state.completionConfirmed
    ? "completed"
    : scenario.status === "completed"
      ? "reviewing"
      : "choosing";

  return {
    status,
    narration: {
      audioPath: payload.narration.audio ?? null,
      text: payload.narration.text
    },
    choices: payload.choices.map((choice) => ({
      audioPath: choice.audio ?? null,
      id: choice.id,
      label: choice.text,
      isSelected: scenario.selectedChoiceId === choice.id
    })),
    feedback: toFeedbackModel(scenario),
    followUp:
      scenario.status === "completed"
        ? scenario.outcome?.followUp === null ||
          scenario.outcome?.followUp === undefined
          ? null
          : {
              audioPath: scenario.outcome.followUp.audio ?? null,
              playbackRequest:
                scenario.attemptCount + scenario.hintState.hintCount,
              text: scenario.outcome.followUp.text
            }
        : null
  };
}

function getMetrics(
  state: FocusSocialFriendshipStoryState
): ActivityCompletionMetrics {
  const metrics = getScenarioAttemptMetrics(state.scenario);

  return {
    completed: state.completionConfirmed,
    score: Math.round(metrics.correctRate * 100),
    correctRate: metrics.correctRate,
    hintCount: metrics.hintCount,
    retryCount: metrics.retryCount
  };
}

function reduceFriendshipStory(
  state: FocusSocialFriendshipStoryState,
  payload: ScenarioPayload,
  action: FocusSocialFriendshipStoryAction
): FocusSocialFriendshipStoryState {
  switch (action.type) {
    case "choose": {
      const scenario = reduceScenario(state.scenario, action, payload);

      return scenario === state.scenario
        ? state
        : { scenario, completionConfirmed: false };
    }
    case "reset":
      return createFriendshipStoryState();
    case "request-hint":
      return state.scenario.status === "completed"
        ? state
        : {
            ...state,
            scenario: {
              ...state.scenario,
              hintState: advanceHintEscalation(state.scenario.hintState),
              outcome: null
            }
          };
    case "continue":
      return state.scenario.status !== "completed" ||
        state.completionConfirmed
        ? state
        : { ...state, completionConfirmed: true };
  }
}

export const focusSocialFriendshipStoryAdapter: ActivityAdapter<
  ScenarioPayload,
  FocusSocialFriendshipStoryState,
  FocusSocialFriendshipStoryAction,
  FocusSocialFriendshipStoryRenderModel
> = {
  activityType: "friendship-stories",
  parsePayload: (value) => scenarioPayloadSchema.parse(value),
  createInitialState: (payload, context) => {
    void payload;
    void context;
    return createFriendshipStoryState();
  },
  reduce: (state, payload, action, context) => {
    void context;
    return reduceFriendshipStory(state, payload, action);
  },
  getMetrics: (state, payload) => {
    void payload;
    return getMetrics(state);
  },
  toRenderModel
};
